import { useEffect, useRef, useState } from 'react'
import {
  Platform,
  type LayoutChangeEvent,
  type LayoutRectangle,
  type NativeSyntheticEvent,
  useWindowDimensions,
} from 'react-native'
import { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { assignAnimatedValue } from '@/src/general/utils/assignAnimatedValue'

const isWebEvent = (value: LayoutRectangle | { top: number }): value is { top: number } => {
  return 'top' in value
}

const HORIZONTAL_PADDING = 48
const BACK_BUTTON_WIDTH = 54
const TRANSLATE_X_FINAL = 44
const TOP_WEB_MARGIN = Platform.OS === 'ios' || Platform.OS === 'android' ? 0 : 20

export const useAnimatedSearchBar = () => {
  const [top, setTop] = useState<number>()
  const translateY = useSharedValue(0)
  const translateX = useSharedValue(0)
  const animationHasRun = useRef(false)
  const isFirstWidthChange = useRef(true)
  const { top: topInset } = useSafeAreaInsets()
  const { width: windowWidth } = useWindowDimensions()

  const initialWidth = windowWidth
  const width = useSharedValue(initialWidth)

  useEffect(() => {
    if (animationHasRun.current || top === undefined) return
    animationHasRun.current = true
    assignAnimatedValue(width, withTiming(initialWidth - HORIZONTAL_PADDING - BACK_BUTTON_WIDTH))
    assignAnimatedValue(translateY, withTiming(-(top - TOP_WEB_MARGIN - topInset)))
    assignAnimatedValue(translateX, withTiming(TRANSLATE_X_FINAL))
  }, [initialWidth, top, translateX, translateY, width, topInset])

  useEffect(() => {
    if (Platform.OS !== 'web') return
    if (isFirstWidthChange.current) {
      isFirstWidthChange.current = false
      return
    }
    assignAnimatedValue(width, windowWidth - HORIZONTAL_PADDING - BACK_BUTTON_WIDTH)
  }, [windowWidth, width])

  const measureView = (event: LayoutChangeEvent | NativeSyntheticEvent<{ layout: { top: number } }>) => {
    if ((Platform.OS === 'ios' || Platform.OS === 'android') && top == null) {
      // oxlint-disable-next-line max-params
      event.target.measure((_x, _y, _width, _height, _pageX, pageY) => {
        setTop(pageY)
      })
    } else if (top == null && isWebEvent(event.nativeEvent.layout)) {
      const topFromEvent = event.nativeEvent.layout.top
      setTop(() => topFromEvent)
    }
  }

  const animatedStyles = useAnimatedStyle(() => ({
    width: width.value,
    transform: [{ translateY: translateY.value }, { translateX: translateX.value }],
  }))

  return { animatedStyles, measureView }
}
